import {
  DEFAULT_IMAGE_MODEL,
  DEFAULT_MASTER_PROMPT,
  getImageModel,
  type ArchitecturalAnalysis,
  type ColorParam,
  type Generation,
  type ImageModelId,
  type LandscapingConfig,
} from '@paint-ai/shared';
import { DEFAULT_LANDSCAPING } from '../data/landscaping';
import { PRESET_PALETTES } from '../data/presetPalettes';
import { SAMPLE_BUILDINGS } from '../data/sampleBuildings';

export type ColorSlot = 'wall' | 'border' | 'highlight';
export type ColorScheme = Record<ColorSlot, ColorParam>;

export interface RenderResult {
  renderUrl: string;
  analysis: ArchitecturalAnalysis | null;
}

export interface StudioState {
  /** Displayable source photo: a data URL (upload/sample) or a stored image URL (from history). */
  image: string;
  colors: ColorScheme;
  /** Editable directives template; [WALL_HEX]-style placeholders are filled when rendering. */
  masterPrompt: string;
  model: ImageModelId;
  landscaping: LandscapingConfig;
  result: RenderResult | null;
}

export type StudioAction =
  | { type: 'imageSelected'; image: string }
  | { type: 'colorChanged'; slot: ColorSlot; color: ColorParam }
  | { type: 'masterPromptChanged'; masterPrompt: string }
  | { type: 'modelChanged'; model: ImageModelId }
  | { type: 'landscapingChanged'; landscaping: LandscapingConfig }
  | { type: 'renderSucceeded'; sourceImage: string; generation: Generation }
  | { type: 'generationLoaded'; generation: Generation }
  | { type: 'reset' };

export function createInitialStudioState(): StudioState {
  const palette = PRESET_PALETTES[0];
  return {
    image: SAMPLE_BUILDINGS[0].imageDataUrl,
    colors: { wall: palette.wall, border: palette.border, highlight: palette.highlight },
    masterPrompt: DEFAULT_MASTER_PROMPT,
    model: DEFAULT_IMAGE_MODEL,
    landscaping: DEFAULT_LANDSCAPING,
    result: null,
  };
}

const toResult = (generation: Generation): RenderResult => ({
  renderUrl: generation.renderImageUrl,
  analysis: generation.analysis,
});

export function studioReducer(state: StudioState, action: StudioAction): StudioState {
  switch (action.type) {
    case 'imageSelected':
      // A new photo invalidates the current render.
      return { ...state, image: action.image, result: null };
    case 'colorChanged':
      return { ...state, colors: { ...state.colors, [action.slot]: action.color } };
    case 'masterPromptChanged':
      return { ...state, masterPrompt: action.masterPrompt };
    case 'modelChanged':
      return { ...state, model: action.model };
    case 'landscapingChanged':
      return { ...state, landscaping: action.landscaping };
    case 'renderSucceeded':
      // The user switched photos while this one rendered: keep their new selection (the render is in history).
      if (state.image !== action.sourceImage) return state;
      return { ...state, result: toResult(action.generation) };
    case 'generationLoaded': {
      const { generation } = action;
      const model = getImageModel(generation.model);
      return {
        image: generation.originalImageUrl,
        colors: { wall: generation.wall, border: generation.border, highlight: generation.highlight },
        masterPrompt: generation.masterPrompt,
        landscaping: generation.landscaping ?? state.landscaping,
        model: model?.selectable ? model.id : state.model,
        result: toResult(generation),
      };
    }
    case 'reset':
      return createInitialStudioState();
  }
}
